import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { FrontEquipmentService } from '../../local-services/front-equipment.service';
import { forkJoin } from 'rxjs';
import { catchError, concatMap, filter, tap, switchMap, startWith, map } from 'rxjs/operators';
import { mergeMap } from 'rxjs/operators';
import { timer } from 'rxjs';
import { of } from 'rxjs';
import { EquipmentActions } from './equipment.actions';
import { EquipmentService } from '../../api-services/equipment/equipment.service';
import { Router } from '@angular/router';
import { EquipmentClaimService } from '../../api-services/equipment/equipment_claim.service';
import { EquipmentFirmwareService } from '../../api-services/equipment/equipment_firmware.service';
import { AppActions } from '../app/app.actions';
import { EquipmentSignalService } from '../../api-services/equipment/equipment_signal.service';
import { EquipmentConnectionsService } from '../../api-services/equipment/equipment_connections.service';
import { SimcardService } from '../../api-services/simcard.service';
import { saveAs } from 'file-saver';
import { EquipmentConfigService } from '../../api-services/equipment/equipment_config.service';
import { EquipmentUpgradeService } from '../../api-services/equipment/equipment_upgrade.service';
import { EquipmentAcmRemoteService } from '../../api-services/equipment/equipment_acm_remote.service';

@Injectable()
export class EquipmentEffects {
  private actions$ = inject(Actions);

  private frontEquipmentService = inject(FrontEquipmentService);

  constructor(
    private router: Router,
    private equipmentService: EquipmentService,
    private equipmentFirmwareService: EquipmentFirmwareService,
    private equipmentUpgradeService: EquipmentUpgradeService,
    private equipmentClaimService: EquipmentClaimService,
    private equipmentSignalService: EquipmentSignalService,
    private equipmentConnectionsService: EquipmentConnectionsService,
    private simcardService: SimcardService,
    private configService: EquipmentConfigService,
    private equipmentACMRemoteService: EquipmentAcmRemoteService,
  ) { }

  loadEquipments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.loadEquipments),
      concatMap((action) =>
        this.equipmentService.getEquipments(action.queryParams, action.page).pipe(
          concatMap((data) => {
            if (data && Array.isArray(data.equipments) && data.equipments.length > 0) {
              // Pass each equipment through interpretEquipment
              return forkJoin(
                data.equipments.map(equip => this.frontEquipmentService.interpretEquipment(equip))
              ).pipe(
                switchMap(interpretedEquipments => [
                  EquipmentActions.loadEquipmentsSuccess({ data: { count: data.count, equipments: interpretedEquipments } }),
                  AppActions.setLoading({ loading: false })
                ])
              );
            } else {
              return [
                EquipmentActions.loadEquipmentsSuccess({ data: { count: data?.count || 0, equipments: [] } }),
                AppActions.setLoading({ loading: false })
              ];
            }
          }),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        ))
    );
  });

  loadEquipmentById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.loadEquipmentById),
      concatMap((action) =>
        this.equipmentService.getEquipmentById(action.id).pipe(
          switchMap((data) =>
            this.frontEquipmentService.interpretEquipment(data).pipe(
              switchMap((interpretedData) => [
                EquipmentActions.loadEquipmentByIdSuccess({
                  data: interpretedData,
                  ...(action.navigateToDetails ? { navigateToDetails: true } : {}),
                }),
                AppActions.setLoading({ loading: false }),
              ])
            )
          ),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  updateEquipmentName$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.updateEquipmentName),
      concatMap(({ payload }) =>
        this.equipmentService.updateEquipment(payload).pipe(
          switchMap(() => [
            EquipmentActions.updateEquipmentNameSuccess({ data: payload }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  updateEquipmentNameOnList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.updateEquipmentNameOnList),
      concatMap(({ payload }) =>
        this.equipmentService.updateEquipment(payload).pipe(
          switchMap(() => [
            EquipmentActions.updateEquipmentNameOnListSuccess({ data: payload }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  updateEquipmentSimcard$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.updateEquipmentSimcard),
      concatMap(({ payload }) =>
        this.simcardService.updateSimcard(payload).pipe(
          switchMap(() => [
            EquipmentActions.updateEquipmentSimcardSuccess({ data: payload }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadEquipmentConnectionHistory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.loadEquipmentConnectionHistory),
      concatMap(({ id }) =>
        this.equipmentConnectionsService.getConnectionHistory(id).pipe(
          switchMap((data) => [
            EquipmentActions.loadEquipmentConnectionHistorySuccess({ data }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
        )
      )
    );
  });

  loadEquipmentSignalHistory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.loadEquipmentSignalHistory),
      concatMap(({ id }) =>
        this.equipmentSignalService.getSignalLevelHistory(id).pipe(
          switchMap((data) => [
            EquipmentActions.loadEquipmentSignalHistorySuccess({ data }),
            // AppActions.setLoading({ loading: false })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          // startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadHardwareList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.loadHardwareList),
      concatMap(() =>
        this.equipmentFirmwareService.getHardwareList().pipe(
          switchMap((data) => [
            EquipmentActions.loadHardwareListSuccess({ data }),
            // AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          // startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadFirmwareList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.loadFirmwareList),
      concatMap((action) =>
        this.equipmentFirmwareService.getFirmwareList(action.hardware).pipe(
          switchMap((data) => [
            EquipmentActions.loadFirmwareListSuccess({ data }),
            // AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          // startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadVersionList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.loadVersionList),
      concatMap((action) =>
        this.equipmentFirmwareService
          .getVersionList(action.hardware, action.firmware)
          .pipe(
            switchMap((data) => [
              EquipmentActions.loadVersionListSuccess({ data }),
              // AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            // startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  downloadFirmware$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.downloadFirmware),
      concatMap((action) =>
        this.equipmentFirmwareService
          .getFirmwareFile(action.hardware, action.firmware, action.version, action.platform)
          .pipe(
            tap((blob) => { saveAs(blob, `${action.hardware}_${action.firmware}_${action.version}.exe`); }),
            switchMap((data) => [
              EquipmentActions.downloadFirmwareSuccess({ data }),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  requestFirmwareUpgrade$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.requestFirmwareUpgrade),
      concatMap(({ equipmentId, upgradeRequest }) =>
        this.equipmentUpgradeService.requestUpgrade(equipmentId, upgradeRequest).pipe(
          switchMap((data) => [
            EquipmentActions.requestFirmwareUpgradeSuccess({ data }),
            EquipmentActions.readPendingFirmwareUpgrade({ equipmentId }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  readPendingFirmwareUpgrade$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.readPendingFirmwareUpgrade),
      concatMap(({ equipmentId }) =>
        this.equipmentUpgradeService.getPendingUpgrades(equipmentId).pipe(
          switchMap((data) => [
            EquipmentActions.readPendingFirmwareUpgradeSuccess({ data }),
            // AppActions.setLoading({ loading: false })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          // startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  cancelFirmwareUpgrade$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.cancelFirmwareUpgrade),
      concatMap(({ equipmentId }) =>
        this.equipmentUpgradeService.cancelPendingUpgrades(equipmentId).pipe(
          switchMap((data) => [
            EquipmentActions.cancelFirmwareUpgradeSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            EquipmentActions.readPendingFirmwareUpgrade({ equipmentId })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  searchEquipment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.searchEquipment),
      concatMap((action) =>
        this.equipmentClaimService
          .searchForClaimEquipment(
            action.payload.sn
          )
          .pipe(
            map((data) => EquipmentActions.searchEquipmentSuccess({ data })),
            catchError((error) => of(EquipmentActions.searchEquipmentFailure({ error })))
          )
      )
    );
  });

  claimEquipment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.claimEquipment),
      concatMap((action) => {
        const { ids, dealerId } = action.payload;
        // Call the claim service for each id and aggregate results
        return forkJoin(ids.map(id => this.equipmentClaimService.claimEquipment(id, dealerId))).pipe(
          // Pass each claimed equipment through interpretEquipment
          concatMap((data) => {
            if (data && Array.isArray(data) && data.length > 0) {
              return forkJoin(
                data.map(equip => this.frontEquipmentService.interpretEquipment(equip))
              ).pipe(
                switchMap(interpretedEquipments => [
                  EquipmentActions.claimEquipmentSuccess({ data: interpretedEquipments, navigateToDetails: action.navigateToDetails }),
                  EquipmentActions.loadEquipments({}),
                  AppActions.setLoading({ loading: false })
                ])
              );
            } else {
              return [
                EquipmentActions.claimEquipmentSuccess({ data: [], navigateToDetails: action.navigateToDetails }),
                EquipmentActions.loadEquipments({}),
                AppActions.setLoading({ loading: false })
              ];
            }
          }),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        );
      })
    );
  });
  loadDetailsAfterClaimEquipment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.claimEquipmentSuccess),
      filter(({ data, navigateToDetails }) => data.length === 1 && navigateToDetails !== false),
      map(({ data }) => EquipmentActions.loadEquipmentById({
        id: data[0].id,
        navigateToDetails: true,
      })),
    )
  );

  navigateToEquipmentDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EquipmentActions.loadEquipmentByIdSuccess),
        filter(({ navigateToDetails }) => navigateToDetails === true),
        tap(() => this.router.navigate(['/main/equipments/details']))
      ),
    { dispatch: false }
  );

  unassignEquipmentFromDealer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.unassignEquipmentFromDealer),
      concatMap(({ payload }) =>
        this.equipmentService.unassignEquipmentFromDealer(payload.id).pipe(
          switchMap((data) => [
            EquipmentActions.unassignEquipmentFromDealerSuccess({ id: payload.id, data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadEquipmentConfiguration$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.loadEquipmentConfiguration),
      concatMap((action) =>
        this.configService.getConfig(action.equipmentId).pipe(
          switchMap(data => {
            // Transformar los datos en un objeto con claves y valores
            const unsortedConfig = data.reduce((acc, item) => {
              const pairs = item.config.value.split(';');
              pairs.forEach((pair: any) => {
                if (pair) {
                  const [key, value] = pair.split(':');
                  // Only set the key if it hasn't been set before
                  if (!(key in acc)) {
                    acc[key] = value;
                  }
                }
              });
              return acc;
            }, {} as Record<string, string>);

            // Ordenar alfabéticamente las claves
            const sortedConfig = Object.keys(unsortedConfig)
              .sort()
              .reduce((acc, key) => {
                acc[key] = unsortedConfig[key];
                return acc;
              }, {} as Record<string, string>);

            // Get utt from the last ConfigResponse
            const configurationUtt = Array.isArray(data) && data.length > 0 ? data[data.length - 1].utt : undefined;

            return [
              EquipmentActions.loadEquipmentConfigurationSuccess({ data: sortedConfig, configurationUtt }),
              // AppActions.setLoading({ loading: false })
            ];
          }),
          catchError(error => of(AppActions.setError({ error }))),
          // startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  requestNTConfiguration$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.requestNTConfiguration),
      concatMap((action) =>
        this.configService.readConfigFromEquipment(action.equipmentId).pipe(
          switchMap(() => [
            EquipmentActions.requestNTConfigurationSuccess(action),
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });
  // Automatically dispatch loadEquipmentConfiguration 10 seconds after requestNTConfigurationSuccess (time for the equipment to respond the config)
  autoLoadEquipmentConfiguration$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.requestNTConfigurationSuccess),
      concatMap((action) =>
        timer(10000).pipe(
          mergeMap(() => [
            EquipmentActions.loadEquipmentConfiguration({ equipmentId: action.equipmentId }),
            AppActions.setLoading({ loading: false })
          ])
        )
      )
    );
  });

  sendNTConfiguration$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.sendNTConfiguration),
      concatMap((action) =>
        this.configService.postConfig(action.equipmentId, action.command).pipe(
          map(() => EquipmentActions.sendNTConfigurationSuccess({ equipmentId: action.equipmentId })),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  // Automatically dispatch loadEquipmentConfiguration 3 seconds after sendNTConfigurationSuccess (time for the equipment to respond the config)
  autoReadEquipmentConfigurationAfterSend$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.sendNTConfigurationSuccess),
      concatMap((action) =>
        timer(3000).pipe(
          mergeMap(() => [
            EquipmentActions.loadEquipmentConfiguration({ equipmentId: action.equipmentId }),
            AppActions.setLoading({ loading: false })
          ])
        )
      )
    );
  });

  loadACMRemotes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.loadACMRemotes),
      concatMap(() =>
        this.equipmentACMRemoteService.getACMRemotes()
          .pipe(
            switchMap((data) => [
              EquipmentActions.loadACMRemotesSuccess({ data }),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  switchDiscoveryMode$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.switchDiscoveryMode),
      concatMap((action) =>
        this.equipmentACMRemoteService.discoverACMRemotes(action.active)
          .pipe(
            switchMap((data) => [
              EquipmentActions.switchDiscoveryModeSuccess({ data }),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  loadACMRemoteById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.loadACMRemoteById),
      concatMap((action) =>
        this.equipmentACMRemoteService.getACMRemoteById(action.acmRemoteId)
          .pipe(
            switchMap((data) => [
              EquipmentActions.loadACMRemoteByIdSuccess({ data }),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });



  createACMRemote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.createACMRemote),
      concatMap((action) =>
        this.equipmentACMRemoteService.createACMRemote(action.data)
          .pipe(
            switchMap((data) => [
              EquipmentActions.createACMRemoteSuccess({ data }),
              EquipmentActions.loadACMRemotes(),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  updateACMRemote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.updateACMRemote),
      concatMap((action) =>
        this.equipmentACMRemoteService.updateACMRemote(action.data)
          .pipe(
            switchMap((data) => [
              EquipmentActions.updateACMRemoteSuccess({ data }),
              EquipmentActions.loadACMRemotes(),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  deleteACMRemote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EquipmentActions.deleteACMRemote),
      concatMap((action) =>
        this.equipmentACMRemoteService.deleteACMRemote(action.acmRemoteId)
          .pipe(
            switchMap(() => [
              EquipmentActions.deleteACMRemoteSuccess(),
              EquipmentActions.loadACMRemotes(),
              AppActions.setLoading({ loading: false }),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

}